import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { BaseServices } from '../../../kuba.services';
import {
  BestWorstCategoryDto,
  DepartmentAverageDto,
  ResponseRateDto,
  SurveyAverageDto,
  SurveyInsightsDto
} from '../analysis/models';
import {
  scoreBarDataset,
  scoreLineDataset,
  scoreScaleOptions
} from '../analysis/chart-theme.util';
import { WorkEnvironmentAnalysisService } from '../analysis/services/work-environment-analysis.service';

/**
 * Result summary for one survey: overall score, department comparison, history
 * across surveys, response rate, and the strongest and weakest category.
 *
 * Ported from the AMIL `Home` page. Three of its panels had no backend behind
 * them — the benchmark index, the honesty gauge and the insight suggestions
 * were literals in the JSX. They are kept, driven from `getInsights`, which is
 * mock-only; see `SurveyInsightsDto`.
 */
@Component({
  selector: 'survey-overview',
  templateUrl: './survey-overview.component.html',
  styleUrls: ['./survey-overview.component.scss'],
  standalone: false
})
export class SurveyOverviewComponent implements OnChanges, OnDestroy {
  /** The survey to summarise. Null while the business has no surveys. */
  @Input() surveyId: number | null = null;

  //#region Variables
  private readonly businessId = BaseServices.BusinessId;

  isLoading = false;
  hasData = false;

  surveyAverage: SurveyAverageDto | null = null;
  responseRate: ResponseRateDto | null = null;
  bestWorst: BestWorstCategoryDto | null = null;
  insights: SurveyInsightsDto | null = null;

  departmentChartData: any = null;
  departmentChartOptions: any = null;
  historyChartData: any = null;
  historyChartOptions: any = null;

  private subscriptions: Subscription[] = [];
  //#endregion

  constructor(
    private analysisService: WorkEnvironmentAnalysisService,
    private translate: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('surveyId' in changes) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  //#region Data
  private load(): void {
    if (!this.surveyId) {
      this.hasData = false;
      return;
    }

    const surveyId = this.surveyId;
    this.isLoading = true;

    this.subscriptions.push(
      forkJoin({
        average: this.analysisService.getSurveyAverage(surveyId),
        departments: this.analysisService.getDepartmentAverages(surveyId),
        history: this.analysisService.getHistoricalAverages(this.businessId),
        responseRate: this.analysisService.getResponseRate(surveyId),
        bestWorst: this.analysisService.getBestAndWorstCategories(surveyId),
        insights: this.analysisService.getInsights(surveyId)
      }).subscribe({
        next: result => {
          this.surveyAverage = result.average;
          this.responseRate = result.responseRate;
          this.bestWorst = result.bestWorst;
          this.insights = result.insights;
          this.buildDepartmentChart(result.departments);
          this.buildHistoryChart(result.history);
          this.hasData = true;
          this.isLoading = false;
        },
        error: () => {
          this.hasData = false;
          this.isLoading = false;
        }
      })
    );
  }
  //#endregion

  //#region Charts
  /** Average score per department for the selected survey. */
  private buildDepartmentChart(departments: DepartmentAverageDto[]): void {
    if (!departments.length) {
      this.departmentChartData = null;
      return;
    }

    this.departmentChartData = {
      labels: departments.map(department => department.DepartmentName),
      datasets: [
        scoreBarDataset(
          this.translate.instant('WORK_ENVIRONMENT_SURVEY.AVERAGE_SCORE'),
          departments.map(department => department.AverageScore)
        )
      ]
    };

    this.departmentChartOptions = scoreScaleOptions(5);
  }

  /** Overall score of every survey the business has run, oldest first. */
  private buildHistoryChart(history: SurveyAverageDto[]): void {
    if (!history.length) {
      this.historyChartData = null;
      return;
    }

    this.historyChartData = {
      labels: history.map(entry => entry.SurveyDate ?? ''),
      datasets: [
        scoreLineDataset(
          this.translate.instant('WORK_ENVIRONMENT_SURVEY.AVERAGE_SCORE'),
          history.map(entry => entry.AverageScore)
        )
      ]
    };

    this.historyChartOptions = scoreScaleOptions(5);
  }
  //#endregion
}
